import { isAmbiguousRequestError } from './reject-join-request-flow'

export type TAcceptMembershipRequestOutcome =
    | { status: 'accepted' }
    | { status: 'accepted_refresh_failed'; error: unknown }
    | { status: 'unconfirmed'; clerkError: unknown }
    | { status: 'unconfirmed_and_refresh_failed'; clerkError: unknown; refreshError: unknown }

export const ACCEPTED_MEMBERSHIP_REQUEST_REFRESH_WARNING =
    'Request accepted, but the page could not refresh. Reload to see the latest status.'

export const UNCONFIRMED_MEMBERSHIP_ACCEPT_WARNING =
    "We couldn't confirm whether the request was accepted. Reload the page before trying again."

interface IAcceptedMembershipRequestCommit {
    id: string
    organizationId: string
    status: 'accepted'
}

function isExactAcceptedRequest(
    result: unknown,
    membershipRequestId: string | undefined,
    clerkOrganizationId: string | undefined
): result is IAcceptedMembershipRequestCommit {
    if (!membershipRequestId || !clerkOrganizationId) return false
    if (typeof result !== 'object' || result === null) return false
    const candidate = result as { id?: unknown; organizationId?: unknown; status?: unknown }
    return (
        candidate.id === membershipRequestId &&
        candidate.organizationId === clerkOrganizationId &&
        candidate.status === 'accepted'
    )
}

/**
 * Keeps Clerk's committed acceptance separate from the best-effort UI refresh that follows it.
 * A refresh failure must never be presented as though the membership request itself failed, and
 * an ambiguous Clerk error only becomes a failure once the exact resource reload cannot prove
 * the commit — mirroring the rejection flow's commit-then-error handling.
 */
export async function acceptMembershipRequestWithRefresh({
    acceptInClerk,
    refreshRequests,
    membershipRequestId,
    clerkOrganizationId,
    readAcceptedInClerk,
}: {
    acceptInClerk: () => Promise<unknown>
    refreshRequests: () => Promise<void>
    membershipRequestId?: string
    clerkOrganizationId?: string
    /** Exact request-resource reload from Clerk's Frontend API; never a pending-list inference. */
    readAcceptedInClerk?: () => Promise<unknown>
}): Promise<TAcceptMembershipRequestOutcome> {
    try {
        await acceptInClerk()
    } catch (clerkError) {
        // Commit-then-error: prove the exact accepted resource before reporting failure. This
        // also converges a deterministic "already accepted" response from a duplicate click.
        let reloaded: unknown = null
        try {
            reloaded = (await readAcceptedInClerk?.()) ?? null
        } catch {
            reloaded = null
        }
        if (!isExactAcceptedRequest(reloaded, membershipRequestId, clerkOrganizationId)) {
            if (!isAmbiguousRequestError(clerkError)) throw clerkError
            try {
                await refreshRequests()
                return { status: 'unconfirmed', clerkError }
            } catch (refreshError) {
                return { status: 'unconfirmed_and_refresh_failed', clerkError, refreshError }
            }
        }
    }

    try {
        await refreshRequests()
        return { status: 'accepted' }
    } catch (error) {
        return { status: 'accepted_refresh_failed', error }
    }
}

/** Returns an honest success, unknown, or partial-success toast for the Clerk acceptance attempt. */
export function getMembershipAcceptanceToast(outcome: TAcceptMembershipRequestOutcome) {
    switch (outcome.status) {
        case 'accepted':
            return { kind: 'success' as const, message: 'Request accepted' }
        case 'accepted_refresh_failed':
            return { kind: 'warning' as const, message: ACCEPTED_MEMBERSHIP_REQUEST_REFRESH_WARNING }
        case 'unconfirmed':
            return { kind: 'warning' as const, message: UNCONFIRMED_MEMBERSHIP_ACCEPT_WARNING }
        case 'unconfirmed_and_refresh_failed':
            return {
                kind: 'warning' as const,
                message:
                    "We couldn't confirm whether the request was accepted, and this list could not refresh. Reload the page before trying again.",
            }
    }
}
